import React, {useEffect, useMemo, useState} from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {Spinner} from '../../Spinner';
import {useFicha21, useUiVersion} from '../../../context/UserModulesContext';
import {BoosterThread} from '../../client/ia_booster/BoosterStage';
import {boosterPathFor, boosterStepsFor, hasBooster} from '../../client/ia_booster/boosterSteps';
import {ArrowDown, CaretRight, Lightning, MagnifyingGlass} from '../../icons';

function initialsOf(name) {
    return String(name || '?')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0])
        .join('')
        .toUpperCase() || '?';
}

/**
 * AI Booster sin cliente elegido (`/ia-booster`, card JCzWrSNo).
 *
 * El caso que describió Pablo: alguien entra a Dashbo y va directo a la sección de AI Booster sin
 * haber elegido cliente. Hasta acá el grupo del sidebar resolvía contra el cliente de la URL y sin
 * cliente mandaba al Listado — que es donde se elige uno, pero no explica nada del Booster ni
 * dice por qué habría que elegir uno *acá*.
 *
 * Lo que hay en pantalla, en el orden del mockup `dashbo-ia-booster-sin-cliente_5.html`: el
 * organismo dormido (mismo lugar y tamaño que el orbe con cliente, sin latido — todavía no hay
 * memoria que respire), qué es el AI Booster en tres pasos, y el buscador de clientes ocupando el
 * lugar de la Barra Total.
 *
 * La lista va AGRUPADA: «Con memoria en uso» arriba —los que ya tienen algo cargado— y «Todavía
 * sin empezar» abajo, más apagados. Pablo: «te deja a mano los que probablemente vas a querer
 * usar». El dato no viene con los clientes (`agency/clients/active` no sabe nada de la memoria, y
 * contar hechos por fila sería un request por cliente): lo trae `agent/client-memory-material/`, un
 * endpoint aparte que resuelve las dos capas en cinco agregaciones.
 *
 * ⚠️ Si ese endpoint no contesta —o el backend todavía no lo tiene deployado— la lista se dibuja
 * PLANA, como antes. Nunca se cae a «Todavía sin empezar»: no saber si un cliente tiene memoria y
 * afirmar que no tiene son cosas distintas, y la segunda es mentirle al usuario sobre su propio
 * trabajo. Es también lo que hace que los dos repos puedan deployar en cualquier orden.
 */
export function AgencyIABoosterContent() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {clientMemoryLevel, modules, loaded} = useFicha21();
    // El marco decide si esta pantalla existe. TODO su CSS cuelga de `.ui-v2`, así que en el
    // Dashbo clásico el markup se dibuja sin un solo estilo: el hilo sale como "1Alimenta2Revisa",
    // la lista de clientes como links sueltos y el lienzo no existe. Verificado en la app (28/08).
    // Es alcanzable sin quererlo —un bookmark, o volver al clásico estando parado acá— así que no
    // alcanza con que el sidebar clásico no la linkee.
    const {isV2, loaded: uiLoaded} = useUiVersion();
    const [clients, setClients] = useState(null);
    const [failed, setFailed] = useState(false);
    const [search, setSearch] = useState('');
    // `{[clientId]: {facts, documents}}` de los clientes que YA tienen memoria. `null` = todavía no
    // llegó, o no se pudo saber: los dos casos dibujan la lista plana. Ver el comentario de arriba.
    const [material, setMaterial] = useState(null);

    useEffect(() => {
        let alive = true;
        new APIClient(API_URL + 'agency/clients/active').call('GET', {}, {cache: true})
            .then((data) => {
                if (!alive) return;
                if (data && data.status === 'OK' && Array.isArray(data.data)) {
                    setClients(data.data);
                } else {
                    setFailed(true);
                }
            })
            .catch(() => { if (alive) setFailed(true); });
        return () => { alive = false; };
    }, []);

    // Quién ya tiene memoria. Va en su propio request y no bloquea a la lista: si tarda o falla, los
    // clientes se ven igual (sin agrupar). Devuelve el objeto directo, sin el envoltorio `{status}`
    // — mismo patrón que los listados de la Memoria.
    useEffect(() => {
        let alive = true;
        new APIClient(API_URL + 'agent/client-memory-material/').call('GET', {}, {cache: true})
            .then((data) => {
                if (!alive || !data || !data.memory_enabled || !Array.isArray(data.clients)) return;
                const byId = {};
                data.clients.forEach((entry) => {
                    byId[String(entry.client_id)] = {
                        facts: entry.facts || 0,
                        documents: entry.documents || 0,
                    };
                });
                setMaterial(byId);
            })
            .catch(() => { /* sin dato: la lista queda plana, que es el comportamiento de siempre */ });
        return () => { alive = false; };
    }, []);

    const access = {clientMemoryLevel, modules};
    const steps = useMemo(() => boosterStepsFor(access),
        [clientMemoryLevel, modules]); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        const list = clients || [];
        const needle = search.trim().toLowerCase();
        if (!needle) return list;
        return list.filter(c => String(c.name || '').toLowerCase().includes(needle));
    }, [clients, search]);

    // Los dos grupos, en el orden del mockup. Sin el dato de memoria devuelve UN solo grupo sin
    // rótulo, que es la lista plana de siempre: agrupar exige saber, y no saber no es "no tiene".
    const groups = useMemo(() => {
        if (!material) return [{key: 'all', labelKey: null, clients: filtered}];
        const inUse = filtered.filter(c => material[String(c.id)]);
        const notStarted = filtered.filter(c => !material[String(c.id)]);
        return [
            {key: 'in_use', labelKey: 'ia_booster.no_client.group_in_use', clients: inUse},
            {key: 'not_started', labelKey: 'ia_booster.no_client.group_not_started', clients: notStarted},
        ].filter(group => group.clients.length > 0);
    }, [filtered, material]);

    // Elegir un cliente entra por el PRIMER PASO QUE ESA AGENCIA TIENE, no por Alimenta fijo.
    // `boosterStepsFor` ya sabe cuál es, y no siempre es el mismo: con la Memoria en OFF y sólo
    // Bitácora el primer paso es Memoria, y con OFF + módulos de IA es «Usa tu IA».
    //
    // Mandar a `/memory/sources` en esos casos no es una URL fea, es una pantalla equivocada:
    // `SUB_VIEWS_BY_LEVEL[OFF]` no incluye 'sources', así que `ClientMemoryTabContent` cae a la
    // primera vista disponible y se renderiza la Bitácora bajo una URL que dice Alimenta — o,
    // sin Bitácora, una pantalla de Memoria sin ninguna vista que mostrar.
    const firstStepPathFor = (clientId) => (steps.length
        ? boosterPathFor(steps[0].key, clientId)
        // `hasBooster` ya gatea la pantalla, así que sin pasos el picker no llegó a montarse.
        // El fallback existe para no navegar a `undefined` si esa invariante cambiara.
        : `/client/${clientId}`);

    // La fila de un cliente. El avatar es la señal de un vistazo: relleno con su punto encendido =
    // esta memoria ya está en uso, contorno = arranca de cero (`-empty`, el estilo que ya existía
    // esperando este dato). Los conteos se muestran sólo si hay: un «0 hechos» al lado de un
    // cliente que sí cargó notas dice algo falso sobre su memoria.
    const renderClient = (client) => {
        const stats = material ? material[String(client.id)] : null;
        const showFacts = stats && stats.facts > 0;
        const showDocs = stats && stats.documents > 0;
        return (
            <button
                key={client.id}
                type="button"
                className={`iab-picker-item${material && !stats ? ' -empty' : ''}`}
                onClick={() => navigate(firstStepPathFor(client.id))}
            >
                <span className="iab-picker-avatar">
                    {initialsOf(client.name)}
                    {stats && <span className="iab-picker-live" aria-hidden="true"/>}
                </span>
                <span className="iab-picker-body">
                    <span className="iab-picker-name">{client.name}</span>
                    {(showFacts || showDocs) && (
                        <span className="iab-picker-meta">
                            {showFacts && (
                                <span className="iab-picker-stat">
                                    <i/>{t('ia_booster.no_client.facts_count', {count: stats.facts})}
                                </span>
                            )}
                            {showFacts && showDocs && <span className="iab-picker-sep" aria-hidden="true"/>}
                            {showDocs && (
                                <span className="iab-picker-stat -docs">
                                    <i/>{t('ia_booster.no_client.documents_count', {count: stats.documents})}
                                </span>
                            )}
                        </span>
                    )}
                </span>
                <CaretRight size={15}/>
            </button>
        );
    };

    // Esperar a los módulos Y al marco antes de decidir: con el default OFF del provider,
    // redirigir sin `loaded` rebota al usuario por un valor que todavía no llegó.
    if (!loaded || !uiLoaded) {
        return (
            <section className="section-content">
                <div className="section-body"><div className="box-loader"><Spinner color="positive"/></div></div>
            </section>
        );
    }
    // Sin memoria, sin bitácora y sin módulos de IA no existe el grupo AI Booster: esta pantalla
    // tampoco. Mismo criterio que el sidebar, resuelto por la misma función. Y en el marco clásico
    // no existe el Recorrido, así que el destino es el Listado — que es donde el clásico elige
    // cliente desde siempre.
    if (!isV2 || !hasBooster(access)) {
        return <Navigate to="/agency/clients" replace/>;
    }

    return (
        <section className="section-content">
            {/* `-page`: acá el escenario es la pantalla entera, no una franja sobre otro contenido. */}
            <div className="iab-stage -page" data-step="alimenta">
                <div className="iab-aurora" aria-hidden="true">
                    <i className="iab-au-alimenta"/>
                    <i className="iab-au-revisa"/>
                    <i className="iab-au-memoria"/>
                    <i className="iab-au-usa"/>
                </div>

                <div className="iab-inner">
                    <BoosterThread steps={steps} clientId={null} locked/>

                    <div className="iab-hero">
                        <div className="iab-idle">
                            <span className="iab-idle-ring" aria-hidden="true"/>
                            <div className="iab-idle-core">
                                <span className="iab-idle-dots" aria-hidden="true"><i/><i/><i/></span>
                                <div className="iab-idle-cap">{t('ia_booster.no_client.title')}</div>
                                <div className="iab-idle-sub">{t('ia_booster.no_client.sub')}</div>
                                <span className="iab-chip">
                                    <ArrowDown size={13} weight="bold"/> {t('ia_booster.no_client.search_hint')}
                                </span>
                            </div>
                        </div>

                        <div className="iab-whatis">
                            <div className="iab-whatis-kicker">{t('ia_booster.whatis.kicker')}</div>
                            {/* La frase del medio va destacada, como en el mockup: es la que dice
                                qué es esto («cada cliente arma su memoria»), y con todo el párrafo
                                al mismo peso hay que leerlo entero para encontrarla. Va en tres
                                claves y no en una con HTML: interpolar markup obliga a apagar el
                                escapado de i18next, y acá no hace falta.
                                ⚠️ El espacio antes de la negrita vive DENTRO de `lede_before` —sí,
                                un espacio final en el JSON, no lo «limpies»—: como `{' '}` en el JSX
                                se lo come un idioma que quiera arrancar por la frase destacada, y el
                                traductor no tiene forma de sacarlo (review C-barra-11). */}
                            <p className="iab-whatis-lede">
                                {t('ia_booster.whatis.lede_before')}
                                <b>{t('ia_booster.whatis.lede_strong')}</b>
                                {t('ia_booster.whatis.lede_after')}
                            </p>
                            <div className="iab-whatis-steps">
                                {['feed', 'review', 'use'].map((key, i) => (
                                    <div key={key} className="iab-whatis-step">
                                        <span className="iab-whatis-num">{i + 1}</span>
                                        <b>{t(`ia_booster.whatis.${key}_title`)}</b>
                                        <span>{t(`ia_booster.whatis.${key}_text`)}</span>
                                    </div>
                                ))}
                            </div>
                            {/* El remate del mockup (`.wi-why`), que la primera pasada no tomó: es
                                la frase que dice para qué sirve todo esto en una línea. Pablo:
                                «no está mal, me parece campeona». */}
                            <div className="iab-whatis-why">
                                <Lightning size={14} weight="fill"/>
                                <span>
                                    {t('ia_booster.whatis.why')}{' '}
                                    <b>{t('ia_booster.whatis.why_strong')}</b>
                                </span>
                            </div>
                        </div>

                        <div className="iab-picker">
                            <label className="iab-picker-search">
                                <MagnifyingGlass size={16}/>
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={t('ia_booster.no_client.search_placeholder')}
                                    aria-label={t('ia_booster.no_client.search_placeholder')}
                                    autoComplete="off"
                                />
                            </label>

                            {clients === null && !failed && (
                                <div className="box-loader"><Spinner color="positive small"/></div>
                            )}
                            {failed && (
                                <p className="iab-picker-empty">{t('ia_booster.no_client.load_error')}</p>
                            )}
                            {clients !== null && (
                                <>
                                    {groups.map(group => (
                                        <React.Fragment key={group.key}>
                                            <div className="iab-picker-label">
                                                {t(group.labelKey || 'ia_booster.no_client.your_clients')}
                                            </div>
                                            <div className={`iab-picker-list${group.key === 'not_started' ? ' -muted' : ''}`}>
                                                {group.clients.map(client => renderClient(client))}
                                            </div>
                                        </React.Fragment>
                                    ))}
                                    {/* Sin resultados NO va el rótulo: sería un encabezado con nada
                                        debajo, que es lo mismo que se evita para los grupos. El
                                        mensaje solo alcanza. */}
                                    {filtered.length === 0 && (
                                        <p className="iab-picker-empty">
                                            {clients.length === 0
                                                ? t('ia_booster.no_client.no_clients')
                                                : t('ia_booster.no_client.no_matches')}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default AgencyIABoosterContent;
